import {
  get,
  compact,
  sum,
  divide,
  max,
  min,
  isNumber,
  isString,
  ceil,
  sumBy,
  sortBy,
  isArray,
  isBoolean,
  isUndefined,
  isNull,
  assign,
} from "lodash";

import { arrayToObject, COLORS, getFormattedDateTimeByLocale } from "@treelab/panda";

import { IChartFormValue, StatisticsType, CalculationType, ContentSortType, OperatorType } from "../types/form-type";
import { ChartType, Language, IBasicData } from "../types/global-type";
import {
  ColumnType,
  ITableColumn,
  ITableData,
  IDateTimeTypeOptions,
  ITableRowData,
  INumberTypeOptions,
  ICurrencyTypeOptions,
  IWrokspaceData,
  IUser,
  ITypeOptions,
} from "../types/table-type";
import { ITooltipFormatterParams } from "../types/echarts-type";

import { checkTimeValid, formatTime, convertDateISOString } from "../lib/day";
import { formatMessage } from "../lib/intl";
import { getCookie } from "../lib/cookies";
import { checkValueInArr } from "./form_secondary";
import { getFormattedNumber, getFormattedCurrency } from "./filter_secondary";
import { getRealColumn } from "./option_main";
import { isDateType } from "./tableData_secondary";

import {
  DIMENSION_COLUMN_TYPE,
  DATE_FORMAT_MAP_EN,
  DATE_FORMAT_MAP_ZH,
  ROLLUP_NUMBER_SPLIT_KEY,
  TABLE_CELL_VALUE_ICON_PENTAGRAM,
  TABLE_CELL_VALUE_ICON_CHECKED,
  TABLE_CELL_VALUE_ICON_UNCHECKED,
} from "../constants/tableData";
import { EXTERNAL_MOUNT_DIV_ID, FORMAT_NAME_SPLICING } from "../constants/app";
import {
  X_AXIS_LABEL_MIN_WIDTH,
  LEGEND_HEIGHT,
  XAXIS_LABEL_LINEHEIGHT,
  IS_USE_AXIS_CHART,
  IS_PIE_CHART,
} from "../constants/chart";
import ConvertTableData from "../class/ConvertTableData";

import { basicSortFunc } from "../utils/sort";

const labelDefaultName = formatMessage({ id: "other.dimension.name.default" });
const locale = getCookie("locale");
const unknownUserName = formatMessage({ id: "other.dimension.unknown.user" });
const nullRecordName = formatMessage({ id: "other.record.unnamed" });

export const getValueByCalculationType = (data: number[], calculationType: CalculationType): number => {
  if (!data || data.length === 0) return 0;
  let result = 0;
  switch (calculationType) {
    case CalculationType.SUM:
      result = sum(data.filter((item) => isNumber(item) && !isNaN(item)));
      break;
    case CalculationType.AVERAGE:
      const notEmptyData = data.filter((item) => isNumber(item) && !isNaN(item));
      result = divide(sum(notEmptyData), notEmptyData.length);
      if (isNaN(result)) result = 0;
      break;
    case CalculationType.MAX:
      result = max(data) || 0;
      break;
    case CalculationType.MIN:
      result = min(data) || 0;
      break;
    default:
      break;
  }
  return result;
};

// 判断是否需要做分组 并返回分组所用列的id
export const checkGroupId = (formValue: IChartFormValue, tableData: ITableData, chartType: ChartType): string => {
  // 从 form 获取 id
  let groupId = "";
  if (IS_USE_AXIS_CHART[chartType]) {
    groupId = formValue.stackByColumnId || "";
  }
  const column = getRealColumn(groupId, tableData);
  if (!column) {
    return "";
  }
  if (!DIMENSION_COLUMN_TYPE[column.type]) {
    return "";
  }
  return groupId;
};

const contentSortFunc: {
  [key in ContentSortType]: (
    list: { [key in string]: string | number }[],
    getValue: (item: object) => number
  ) => { [key in string]: string | number }[];
} = {
  [ContentSortType.X_FIRST_TO_LAST]: (list) => list,
  [ContentSortType.X_LASTT_OF_IRST]: (list) => list.reverse(),
  [ContentSortType.Y_FIRST_TO_LAST]: (list, getValue) => list.sort((item1, item2) => getValue(item1) - getValue(item2)),
  [ContentSortType.Y_LASTT_OF_IRST]: (list, getValue) => list.sort((item1, item2) => getValue(item2) - getValue(item1)),
};
export const sortDatasetByContentSort = (
  list: { [key in string]: string | number }[],
  contentSort: ContentSortType,
  getValue: (item: object) => number
): { [key in string]: string | number }[] => {
  const sortFunc = contentSortFunc[contentSort];
  return sortFunc(list, getValue);
};

const sortGroupDatasetList = (
  list: { [key in string]: string | number }[],
  getValue: (item: object) => number,
  dimensionId: string,
  sortArgument: number
) => {
  const sumObj: { [key in string]: number } = {};
  list.forEach((item: any) => {
    const itemDimension = get(item, dimensionId);
    if (sumObj[itemDimension]) {
      sumObj[itemDimension] += getValue(item);
    } else {
      sumObj[itemDimension] = getValue(item);
    }
  });
  list.sort((item1, item2) =>
    get(sumObj, item1[dimensionId]) > get(sumObj, item2[dimensionId]) ? sortArgument : -sortArgument
  );
  return list;
};

export const sortGroupDatasetByContentSort = (
  list: { [key in string]: string | number }[],
  contentSort: ContentSortType,
  getValue: (item: object) => number,
  dimensionId: string
): { [key in string]: string | number }[] => {
  switch (contentSort) {
    case ContentSortType.X_FIRST_TO_LAST:
      return list;
    case ContentSortType.X_LASTT_OF_IRST:
      return list.reverse();
    case ContentSortType.Y_FIRST_TO_LAST:
      return sortGroupDatasetList(list, getValue, dimensionId, 1);
    case ContentSortType.Y_LASTT_OF_IRST:
      return sortGroupDatasetList(list, getValue, dimensionId, -1);
  }
};

const sortByNumberColumn = (value1?: string | number, value2?: string | number): number => {
  const number1 = isNumber(value1) ? value1 : Number.parseFloat(value1 || "");
  const number2 = isNumber(value2) ? value2 : Number.parseFloat(value2 || "");
  if (isNaN(number1) || isNaN(number2)) {
    return isNaN(number2) ? -1 : 1;
  }
  return number1 > number2 ? 1 : -1;
};

/**
 * 基础排序
 */
export const sortDatasetBasic = (
  list: { [key in string]: string | number }[],
  dimensionColumnType: ColumnType,
  dimensionColumnOption: ITypeOptions,
  valueKey: string,
  isSplit: boolean
) => {
  if (
    checkValueInArr(dimensionColumnType, [ColumnType.SELECT, ColumnType.STATUS]) ||
    (dimensionColumnType === ColumnType.MULTI_SELECT && isSplit)
  ) {
    return setSelectOptionOrder(list);
  }

  const func = basicSortFunc({ columType: dimensionColumnType, columnOptions: dimensionColumnOption, valueKey });

  list.sort(func);
};

// 多选需要支持 option 排序 考虑在计算 dimensionTableData 时 将 多选拆解成多个单选 则此处无需修改就能支持排序
const setSelectOptionOrder = (list: { [key in string]: string | number }[]) => {
  list.sort((item1, item2) => {
    const value1 = get(item1, "order");
    const value2 = get(item2, "order");
    return sortByNumberColumn(value1, value2);
  });
};

/**
 * 转换 source 的结构 {}[] => string[][]
 */
export const convertSource = (list: { [key in string]: number | string }[], keyList: string[]) => {
  const result = list.map((item) => {
    return compact(keyList).map((item2) => item[item2]);
  });
  return result;
};

export const convertFormValueForNot = (value: IChartFormValue, tableData: ITableData): IChartFormValue => {
  const newValue: { [key in string]: string | number } = {};
  const { statisticsType, dimensionId, chartType } = value;
  const dimensionColumn = getRealColumn(dimensionId, tableData);
  if (statisticsType === StatisticsType.COUNT) {
    newValue["calculationTargetId"] = "";
  }
  if (!isDateType(dimensionColumn?.type)) {
    // @ts-ignore
    newValue["isGroupAtCycle"] = false;
  }
  if (
    checkValueInArr(chartType, [ChartType.BASIC_LINE]) &&
    checkValueInArr(dimensionColumn?.type, [
      ColumnType.DATETIME,
      ColumnType.NUMBER,
      ColumnType.RATING,
      ColumnType.CURRENCY,
      ColumnType.CREATED_AT,
    ])
  ) {
    newValue["contentSort"] = ContentSortType.X_FIRST_TO_LAST;
  }
  return { ...value, ...newValue };
};

/**
 * 饼 环 专用 tooltip 要展示 百分比
 * @param color
 * @param name
 * @param value
 * @param percent
 * @returns
 */
const getPieTooltipDom = (
  color: string,
  name: string | number,
  value: string | number,
  percent: string | number,
  canvasWidth: number
) => {
  const container = document.createElement("div");
  container.setAttribute("style", `display: flex;`);
  const marker = document.createElement("span");
  marker.setAttribute(
    "style",
    `display:inline-block;margin-right:8px;width:8px;height: 8px;;border-radius:8px;background-color:${color}; flex-shrink: 0; margin-top: 4px;`
  );
  container.appendChild(marker);
  const mainContainer = document.createElement("div");
  mainContainer.setAttribute("style", "display: flex; flex-direction: column; ");
  const nameSpan = document.createElement("span");
  nameSpan.setAttribute(
    "style",
    `color: ${COLORS.grey};font-size:12px;line-height:16px;margin-right: 8px; text-overflow: ellipsis; white-space: nowrap; overflow: hidden;`
  );
  nameSpan.innerText = `${name}`;
  mainContainer.appendChild(nameSpan);
  const numberSpan = document.createElement("span");
  numberSpan.setAttribute("style", `color: ${COLORS.greyDark1}; font-size: 12px;lien-height: 16px; flex-shrink: 0;`);
  numberSpan.innerText = `${value}`;
  mainContainer.appendChild(numberSpan);
  const percentSpan = document.createElement("span");
  percentSpan.setAttribute("style", `color: ${COLORS.greyDark1}; font-size: 12px;lien-height: 16px; flex-shrink: 0;`);
  percentSpan.innerText = `${percent}%`;
  mainContainer.appendChild(percentSpan);
  container.appendChild(mainContainer);
  return container;
};

const getTooltipIconByChartType = (color: string, chartType: ChartType) => {
  switch (chartType) {
    case ChartType.BASIC_BAR:
    case ChartType.STACKED_COLUMN_BAR: {
      const icon = document.createElement("div");
      icon.setAttribute("style", `border-radius: 1px; width: 8px; height: 6px; background-color: ${color};`);
      return icon;
    }
    case ChartType.BASIC_LINE: {
      const icon = document.createElement("div");
      icon.setAttribute("style", `border-radius: 1px; width: 8px; height: 2px; background-color: ${color};`);
      return icon;
    }
    default:
      return document.createElement("div");
  }
};

const getOneTooltipDomByChartType = (
  name: string,
  value: string | number,
  chartType: ChartType,
  color: string,
  canvasWidth: number
) => {
  const container = document.createElement("div");
  container.setAttribute("style", "");
  const nameDiv = document.createElement("div");
  nameDiv.setAttribute(
    "style",
    `font-size: 12px; line-height: 16px; color: ${COLORS.grey}; overflow: hidden; margin-bottom: 8px;`
  );
  nameDiv.innerText = `${name}`;
  container.appendChild(nameDiv);
  const valueContainer = document.createElement("div");
  valueContainer.setAttribute("style", `display: flex; align-items: center;`);
  valueContainer.appendChild(getTooltipIconByChartType(color, chartType));
  const valueSpan = document.createElement("span");
  valueSpan.setAttribute("style", `margin-left: 8px; font-size: 12px; line-height: 16px; color: ${COLORS.greyDark1};`);
  valueSpan.innerText = `${value}`;
  valueContainer.appendChild(valueSpan);
  container.appendChild(valueContainer);
  return container;
};

const getMultipleTooltipDomByChartType = (
  name: string,
  list: { name: string; value: string | number; color: string }[],
  chartType: ChartType,
  canvasWidth: number,
  valueSum: string,
  showValueSum?: boolean
) => {
  const container = document.createElement("div");
  container.setAttribute("style", `border-radius: 4px;`);

  const titleContainer = document.createElement("div");
  titleContainer.setAttribute(
    "style",
    `margin-bottom: 8px; color: ${COLORS.grey}; height: 20px; font-size: 12px; line-height: 20px;`
  );
  titleContainer.innerHTML = name;
  container.appendChild(titleContainer);

  if (showValueSum) {
    const countSumContainer = document.createElement("div");
    countSumContainer.setAttribute(
      "style",
      "margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; overflow: hidden; height: 20px;"
    );
    const countSumTitle = document.createElement("div");
    countSumTitle.innerHTML = formatMessage({ id: "chart.tooltip.total" });
    countSumTitle.setAttribute("style", `font-size: 12px; line-height: 16px; color: ${COLORS.grey}`);
    countSumContainer.appendChild(countSumTitle);

    const countSumNumber = document.createElement("div");
    countSumNumber.setAttribute("style", `color: ${COLORS.greyDark1}; font-size: 12px; line-height: 12px;`);
    countSumNumber.innerHTML = `${valueSum}`;
    countSumContainer.appendChild(countSumNumber);
    container.appendChild(countSumContainer);
  }

  const keyList = ["name", "value"];
  const styleByKey = {
    name: "padding-left: 8px; flex-grow: 2;",
    value: "padding-left: 8px; justify-content: flex-end;",
  };
  const listContainer = document.createElement("div");
  listContainer.setAttribute("style", "display: flex; width: 100%; justify-content: space-between;");
  keyList.forEach((item, index) => {
    const lineContainer = document.createElement("div");
    list.forEach((item2) => {
      if (index > 0) {
        const itemDiv = document.createElement("div");
        itemDiv.setAttribute(
          "style",
          `display: flex; align-items: center; height: 20px; font-size: 12px; margin-bottom: 4px; color: ${COLORS.greyDark1
          }; ${get(styleByKey, item)}`
        );
        itemDiv.innerText = get(item2, item);
        lineContainer.appendChild(itemDiv);
      } else {
        const leftDiv = document.createElement("div");
        leftDiv.setAttribute("style", "display: flex;");
        const iconDiv = document.createElement("div");
        iconDiv.setAttribute(
          "style",
          "display: flex; align-items: center; height: 20px; margin-bottom: 4px; flex-grow: 0;"
        );
        iconDiv.appendChild(getTooltipIconByChartType(item2["color"], chartType));
        leftDiv.appendChild(iconDiv);
        const nameDiv = document.createElement("div");
        nameDiv.setAttribute(
          "style",
          `display: flex; align-items: center; font-size: 12px; height: 20px; margin-bottom: 4px; ${get(
            styleByKey,
            item
          )}`
        );
        nameDiv.innerText = get(item2, item);
        leftDiv.appendChild(nameDiv);
        lineContainer.appendChild(leftDiv);
      }
    });
    listContainer.appendChild(lineContainer);
  });
  container.appendChild(listContainer);
  return container;
};

/**
 * 根据 chartType 绘制 tooltip chartType 决定 图例icon
 * @param params
 * @param chartType
 */
export const getTooltipDomByChartType = (
  params: ITooltipFormatterParams | ITooltipFormatterParams[],
  chartType: ChartType,
  canvasWidth: number,
  formatValueFunc: (value: number) => string,
  opt: {
    isGroup?: boolean;
    isShowStackTotal?: boolean;
  }
) => {
  const { isGroup, isShowStackTotal } = opt;
  if (!params || (isArray(params) && params.length === 0)) {
    return null;
  }
  if (IS_PIE_CHART[chartType]) {
    const param = Array.isArray(params) ? params[0] : params;
    const { color, data, percent } = param;
    return getPieTooltipDom(color, data[0], data[1], percent as number, canvasWidth);
  }
  if (isGroup && isArray(params)) {
    const name = params[0].data[0] as string;
    const list = params.map((item) => {
      const { color, data } = item;
      return {
        color,
        name: data[2] as string,
        value: data[1] as number | string,
      };
    });
    const valueSum = formatValueFunc(sumBy(params, "value[1]"));
    return getMultipleTooltipDomByChartType(name, list, chartType, canvasWidth, valueSum, isShowStackTotal);
  } else {
    const param = isArray(params) ? params[0] : params;
    const { color, data } = param;
    return getOneTooltipDomByChartType(data[0] as string, data[1] as number | string, chartType, color, canvasWidth);
  }
};

const getDateFormatMapByLocale = () => {
  const locale = getCookie("locale");

  switch (locale) {
    case Language.en:
      return DATE_FORMAT_MAP_EN;
    case Language.zh:
    default:
      return DATE_FORMAT_MAP_ZH;
  }
};

const formatTimeByColumn = (timeStr: string, opt: IDateTimeTypeOptions): string => {
  const { dateFormat } = opt;
  const dateFormatMap = getDateFormatMapByLocale();
  const formatRule = dateFormatMap[dateFormat];
  const result = formatTime(timeStr, formatRule);
  return result;
};

export const formatDimensionTimeByColumn = (
  list: { [key in string]: string | number }[],
  dimensionId: string,
  column: ITableColumn
): { [key in string]: string | number }[] => {
  const result = list.map((item) => ({
    ...item,
    [dimensionId]: formatTimeByColumn(item[dimensionId] as string, column.typeOptions as IDateTimeTypeOptions),
  }));
  return result;
};

export const getCalculationColumn = (
  tableData: ITableData,
  statisticsType: StatisticsType,
  calculationTargetId?: string
): ITableColumn | undefined => {
  if (statisticsType === StatisticsType.COUNT) return undefined;
  const column = getRealColumn(calculationTargetId || "", tableData);
  if (!column) return undefined;
  return column;
};

/**
 * 将 y 轴的 value 按所属 column 的规则 format 成 string
 * @param value
 * @param column
 */
export const formatValueNumberToString = (value: number, column?: ITableColumn): string => {
  if (!column) return `${value}`;
  switch (column.type) {
    case ColumnType.NUMBER: {
      return getFormattedNumber(value, column?.typeOptions as INumberTypeOptions);
    }
    case ColumnType.RATING:
      if (isNumber(value)) {
        return value.toString().includes(".")
          ? `${value.toFixed(1)}${TABLE_CELL_VALUE_ICON_PENTAGRAM}`
          : `${value}${TABLE_CELL_VALUE_ICON_PENTAGRAM}`;
      }
      return "";
    case ColumnType.CURRENCY: {
      return getFormattedCurrency(value, column?.typeOptions as ICurrencyTypeOptions) || "";
    }
    default:
      return `${value}`;
  }
};

const convertArrayToString = (data: any, convertFunc: (data: any) => string): string => {
  if (!isArray(data)) return convertFunc(data);
  const result = data.map((item) => convertFunc(item)).join(FORMAT_NAME_SPLICING);
  return result;
};

const checkboxValueToString = (value: boolean | undefined | null | string) => {
  if (isString(value)) return value === "true" ? TABLE_CELL_VALUE_ICON_CHECKED : TABLE_CELL_VALUE_ICON_UNCHECKED;
  if (isBoolean(value)) return value ? TABLE_CELL_VALUE_ICON_CHECKED : TABLE_CELL_VALUE_ICON_UNCHECKED;
  return labelDefaultName;
};

const ratingValueToString = (value: number | undefined | null) => {
  if (!isNumber(value)) return "";
  if (value.toString().includes(".")) {
    return `${Number(value).toFixed(1)}${TABLE_CELL_VALUE_ICON_PENTAGRAM}`;
  }
  return `${value}${TABLE_CELL_VALUE_ICON_PENTAGRAM}`;
};

const sortNumberTypeData = (a: number, b: number) => a - b;

/**
 * 转换 维度 label
 * 包括 x轴 group
 * @param value
 * @param column
 */
export const formatDimensionToString = (paramValue: any, column: ITableColumn): string => {
  let value = paramValue;
  if (typeof value === "string" && value.split(",").length > 1) {
    value = value.split(",").map((item) => item.trim());
  }
  if (column.type === ColumnType.CHECKBOX) {
    if (isUndefined(value) || isNull(value)) {
      value = false;
    }
  }
  if (!value && !isNumber(value) && !isBoolean(value)) return labelDefaultName;
  if (isArray(value) && value.length === 0) return labelDefaultName;
  const splitStr = FORMAT_NAME_SPLICING;
  switch (column.type) {
    case ColumnType.DATETIME:
    case ColumnType.CREATED_AT: {
      if (!isArray(value))
        return getFormattedDateTimeByLocale(value, column.typeOptions as IDateTimeTypeOptions, locale);
      const result = value
        .sort()
        .map((item) => getFormattedDateTimeByLocale(item, column.typeOptions as IDateTimeTypeOptions, locale))
        .join(splitStr);
      return result;
    }
    case ColumnType.NUMBER: {
      if (!isArray(value)) return getFormattedNumber(value, column.typeOptions as INumberTypeOptions);
      const result = value
        .sort(sortNumberTypeData)
        .map((item) => getFormattedNumber(item, column.typeOptions as INumberTypeOptions))
        .join(splitStr);
      return result;
    }
    case ColumnType.CURRENCY: {
      // @ts-ignore
      if (!isArray(value)) return getFormattedCurrency(value, column.typeOptions as ICurrencyTypeOptions);
      const result = value
        .sort(sortNumberTypeData)
        .map((item) => getFormattedCurrency(item, column.typeOptions as ICurrencyTypeOptions))
        .join(splitStr);
      return result;
    }
    case ColumnType.STATUS: {
      const {
        typeOptions: { statuses },
      } = column as ITableColumn<ColumnType.STATUS>;
      if (!isArray(value)) return statuses.find((item) => item.statusId === value)?.name || labelDefaultName;
      const statusId = arrayToObject(statuses, "statusId");
      const result = sortBy(value, (data: string) => {
        return statusId[data]?.order || -1;
      })
        .map((item) => statusId[item]?.name || labelDefaultName)
        .join(splitStr);
      return result;
    }
    case ColumnType.SELECT:
    case ColumnType.MULTI_SELECT: {
      const {
        typeOptions: { options },
      } = column as ITableColumn<ColumnType.SELECT>;
      if (!isArray(value)) return options.find((item) => item.optionId === value)?.name || labelDefaultName;
      const optionObj = arrayToObject(options, "optionId");
      const result = sortBy(value, (data: string) => {
        return optionObj[data]?.order || -1;
      })
        .map((item) => optionObj[item]?.name || labelDefaultName)
        .join(splitStr);
      return result;
    }
    case ColumnType.RECORD_REFERENCE: {
      const result = sortBy(isArray(value) ? value : [value], (data: { id: string; visibleName: string }) => data.id)
        .map((item: { visibleName: string }) => item.visibleName || nullRecordName)
        .join(splitStr);
      return result;
    }
    case ColumnType.COLLABORATOR:
    case ColumnType.CREATED_BY: {
      const usersMap = get(window, "treelab.users") as IWrokspaceData["users"];
      const users = Object.values(usersMap.byId) as IUser[];
      if (!isArray(value)) return users.find((item) => item.id === value)?.nickName || unknownUserName;
      const result = sortBy(value, (item) => item)
        .map((item: string) => users.find((item2) => item2.id === item)?.nickName || unknownUserName)
        .join(splitStr);
      return result;
    }
    case ColumnType.RATING: {
      if (!isArray(value)) return ratingValueToString(value);
      return value.sort(sortNumberTypeData).map(ratingValueToString).join(splitStr);
    }
    case ColumnType.CHECKBOX: {
      if (!isArray(value)) return checkboxValueToString(value);
      return sortBy(value, (data: boolean) => (data ? 1 : -1))
        .map(checkboxValueToString)
        .join(splitStr);
    }
    default:
      return `${value}`;
  }
};

const getConvertStringKeyFunc: {
  [key in Exclude<
    ColumnType,
    | ColumnType.LOOKUP
    | ColumnType.MULTI_ATTACHMENT
    | ColumnType.ROLLUP
    | ColumnType.FORMULA
    | ColumnType.LONG_TEXT
    | ColumnType.AUTO_NUMBER
    | ColumnType.NOOP
    | ColumnType.UNIQUE_ID
  >]: (value: any) => string;
} = {
  [ColumnType.DATETIME]: (value) => (checkTimeValid(value) ? value : labelDefaultName),
  [ColumnType.NUMBER]: (value) => (isNumber(value) ? `${value}` : value),
  [ColumnType.CURRENCY]: (value) => (isNumber(value) ? `${value}` : value),
  [ColumnType.STATUS]: (value) => value,
  [ColumnType.SELECT]: (value) => value,
  [ColumnType.MULTI_SELECT]: (value) => value,
  [ColumnType.RECORD_REFERENCE]: (value) => value.id,
  [ColumnType.COLLABORATOR]: (value) => value,
  [ColumnType.RATING]: (value) => (isNumber(value) ? `${value}` : value),
  [ColumnType.CHECKBOX]: checkboxValueToString,
  [ColumnType.TEXT]: (value) => value,
  [ColumnType.EMAIL]: (value) => value,
  [ColumnType.PHONE]: (value) => value,
  [ColumnType.CREATED_AT]: (value) => (checkTimeValid(value) ? value : labelDefaultName),
  [ColumnType.CREATED_BY]: (value) => value,
};

export const formatDimensionToStringKey = (paramValue: any, column: ITableColumn): string => {
  let value = paramValue;
  if (typeof value === "string" && value.split(",").length > 1) {
    value = value.split(",").map((item) => item.trim());
  }
  if (isArray(value) && value.length === 0) return labelDefaultName;
  // 预处理
  if (isUndefined(value) || isNull(value)) {
    value = "";
  }
  if (isArray(value)) {
    switch (column.type) {
      case ColumnType.CHECKBOX:
        value = sortBy(value, (data: boolean) => (data ? 1 : -1));
        break;
      case ColumnType.NUMBER:
      case ColumnType.RATING:
      case ColumnType.CURRENCY:
        value.sort(sortNumberTypeData);
        break;
      case ColumnType.STATUS:
        value = sortBy(value, (data: string) => {
          const {
            typeOptions: { statuses },
          } = column as ITableColumn<ColumnType.STATUS>;
          const optionObj = arrayToObject(statuses, "statusId");
          return optionObj[data]?.order || -1;
        });
        break;
      case ColumnType.SELECT:
      case ColumnType.MULTI_SELECT:
        value = sortBy(value, (data: string) => {
          const {
            typeOptions: { options },
          } = column as ITableColumn<ColumnType.SELECT>;
          const optionObj = arrayToObject(options, "optionId");
          return optionObj[data]?.order || -1;
        });
        break;
      case ColumnType.DATETIME:
      case ColumnType.CREATED_AT:
        value.sort();
        break;
      case ColumnType.RECORD_REFERENCE:
        value = sortBy(value, (data: { id: string; visibleName: string }) => data.id);
        break;
      case ColumnType.COLLABORATOR:
      case ColumnType.CREATED_BY:
        value.sort();
        break;
      default:
        break;
    }
  }
  // @ts-ignore
  const convertFunc = getConvertStringKeyFunc[column.type] as (value: any) => string;
  const result = isArray(value) ? convertArrayToString(value, convertFunc) : convertFunc(value);
  return result;
};

export const getBasicDataByChartFunc = (keyList: string[], tableData: ITableData, valueKey?: string) => {
  const isRollupNumber = new ConvertTableData({
    tableData,
    filterMeta: { filterValues: [], operatorType: OperatorType.AND },
  }).checkRollupNumber(valueKey);
  const func = (row: ITableRowData): IBasicData | IBasicData[] => {
    const { cells } = row;
    const data = keyList.reduce((accumulator: { [key in string]: any }, currentValue: any, index: number) => {
      return {
        ...accumulator,
        [keyList[index]]: get(cells, currentValue),
      };
    }, {});
    let cellVale = isString(valueKey) && valueKey.length > 0 ? get(cells, valueKey) : 1;
    // 当 value column 选择 rollup number 其 cellValue 可能为 string
    if (isRollupNumber && isString(cellVale)) {
      if (cellVale.includes(ROLLUP_NUMBER_SPLIT_KEY)) {
        cellVale = cellVale.split(ROLLUP_NUMBER_SPLIT_KEY);
      }
    }
    if (!isArray(cellVale)) {
      return {
        ...data,
        cellValue: isNumber(cellVale) ? cellVale : isString(cellVale) ? Number.parseFloat(cellVale) : undefined,
      };
    } else if (cellVale.length === 0) {
      return {
        ...data,
        cellValue: undefined,
      };
    } else {
      const result = cellVale.map((item) => ({
        ...data,
        cellValue: isNumber(item)
          ? item
          : isString(item)
            ? !isNaN(Number.parseFloat(item))
              ? Number.parseFloat(item)
              : undefined
            : undefined,
      }));
      return result;
    }
  };
  return func;
};

export const getLabelWidth = (str: string): number => {
  const div = document.getElementById(EXTERNAL_MOUNT_DIV_ID);
  if (!div) return 0;
  div.innerText = str;
  const result = div.clientWidth;
  div.innerText = "";
  return result;
};

export const getLabelsWidth = (list: string[]): number[] => {
  const div = document.getElementById(EXTERNAL_MOUNT_DIV_ID);
  if (!div) return [];
  const widthList = list.map((item) => {
    div.innerText = item;
    return div.clientWidth;
  });
  div.innerText = "";
  return widthList;
};

// 计算 chart 留白
export const getGridLeft = (isExpand: boolean, chartType: ChartType, isShowYAxis: boolean): number => {
  switch (chartType) {
    case ChartType.BASIC_BAR:
    case ChartType.STACKED_COLUMN_BAR:
    case ChartType.BASIC_LINE:
      return isExpand ? (isShowYAxis ? 80 : 32) : isShowYAxis ? 60 : 24;
    default:
      return 0;
  }
};
export const getGridRight = (isExpand: boolean, chartType: ChartType, isLabelRotate: boolean): number => {
  switch (chartType) {
    case ChartType.BASIC_BAR:
    case ChartType.STACKED_COLUMN_BAR:
    case ChartType.BASIC_LINE:
      return (
        (isExpand ? 32 : 24) + (isLabelRotate ? ceil(X_AXIS_LABEL_MIN_WIDTH * (divide(Math.sqrt(3), 2) - 0.5)) : 0)
      );
    default:
      return 0;
  }
};
export const getGridTop = (isExpand: boolean, chartType: ChartType, isShowLegend: boolean): number => {
  switch (chartType) {
    case ChartType.BASIC_BAR:
    case ChartType.STACKED_COLUMN_BAR:
    case ChartType.BASIC_LINE:
      return isExpand ? (isShowLegend ? 60 + LEGEND_HEIGHT : 40) : isShowLegend ? 30 + LEGEND_HEIGHT : 24;
    default:
      return 0;
  }
};
export const getGridBottom = (isExpand: boolean, chartType: ChartType, isLabelRotate: boolean): number => {
  switch (chartType) {
    case ChartType.BASIC_BAR:
    case ChartType.STACKED_COLUMN_BAR:
    case ChartType.BASIC_LINE:
      return isExpand ? 80 : isLabelRotate ? 44 + X_AXIS_LABEL_MIN_WIDTH / 2 - XAXIS_LABEL_LINEHEIGHT : 44;
    default:
      return 0;
  }
};
export const getGridWidth = (
  canvasWidth: number,
  isExpand: boolean,
  chartType: ChartType,
  isShowYAxis: boolean,
  isLabelRotate: boolean
): number => {
  switch (chartType) {
    case ChartType.BASIC_BAR:
    case ChartType.STACKED_COLUMN_BAR:
    case ChartType.BASIC_LINE:
      return (
        canvasWidth - getGridLeft(isExpand, chartType, isShowYAxis) - getGridRight(isExpand, chartType, isLabelRotate)
      );
    default:
      return canvasWidth;
  }
};
export const getGridHeight = (
  canvasHeight: number,
  isExpand: boolean,
  chartType: ChartType,
  isLabelRotate: boolean,
  isShowLegend: boolean
): number => {
  switch (chartType) {
    case ChartType.BASIC_BAR:
    case ChartType.STACKED_COLUMN_BAR:
    case ChartType.BASIC_LINE:
      return (
        canvasHeight - getGridTop(isExpand, chartType, isShowLegend) - getGridBottom(isExpand, chartType, isLabelRotate)
      );
    default:
      return canvasHeight;
  }
};

export const convertDateValue = (list: IBasicData[], columnIds: string[]): IBasicData[] => {
  const newList = list.map((item) => {
    const newColumnValue = columnIds.reduce(
      (accumulatorObj, currColumnId) => ({
        ...accumulatorObj,
        [currColumnId]: item[currColumnId] && convertDateISOString(item[currColumnId]),
      }),
      {}
    );
    return assign({}, item, newColumnValue);
  });
  return newList;
};
